<template>
  <div class="risk-assessment-section" :class="{ 'risk-assessment-section--expanded': isExpanded }">
    <button
      v-if="isCollapsible"
      type="button"
      class="risk-assessment-section-toggle"
      :class="{ 'risk-assessment-section-toggle--expanded': isExpanded }"
      :aria-expanded="String(isExpanded)"
      @click="toggleExpanded"
    >
      <div class="risk-assessment-section-toggle__content">
        <v-icon :color="headerIconColor" class="triage-icon" small>{{ mdiCircle }}</v-icon>
        <mrx-translation class="treatment-header" :token="headerToken" />
      </div>

      <div class="risk-assessment-section-toggle__actions">
        <v-icon
          :size="24"
          color="primary"
          class="risk-assessment-section-toggle__chevron"
          :class="{ 'risk-assessment-section-toggle__chevron--expanded': isExpanded }"
        >
          {{ mdiChevronDown }}
        </v-icon>
      </div>
    </button>

    <div
      class="risk-assessment-section-body"
      :class="{
        'risk-assessment-section-body--visible': !isCollapsible || isExpanded,
        'risk-assessment-section-body--hidden': isCollapsible && !isExpanded,
      }"
    >
      <div class="risk-assessment-section-body__inner">
        <RiskAssessmentRow
          v-for="(contentItem, index) in contentList"
          :key="`${contentItem.name}_${contentItem.headerToken}`"
          :content-item="contentItem"
          :header-token="headerToken"
          :header-icon-color="headerIconColor"
          :is-checklist="isChecklist"
          :checkbox-icon="checkboxIcon(contentItem.name)"
          @select="handleSelect"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import MrxTranslation from "@/components/thinkmd/mrx/translation.vue";
import { mdiCheckboxBlankOutline, mdiCheckboxOutline, mdiChevronDown, mdiCircle } from "@mdi/js";
import { defineComponent, PropType, ref, watch } from "vue";
import { ContentItem } from "../../types/overall-assessment.types";
import RiskAssessmentRow from "./RiskAssessmentRow.vue";

export default defineComponent({
  name: "RiskAssessmentItem",
  components: { MrxTranslation, RiskAssessmentRow },
  emits: ["input", "handleInput"],
  props: {
    headerToken: {
      type: String,
      default: "",
    },
    headerIconColor: {
      type: String,
      default: "",
    },
    contentList: {
      type: Array as PropType<ContentItem[]>,
      default: () => [],
    },
    isChecklist: {type: Boolean, default: false},
    value: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    isCollapsible: {
      type: Boolean,
      default: false,
    },
    initiallyCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const selected = ref<string[]>([...props.value]);
    const isExpanded = ref(!props.initiallyCollapsed);

    watch(
      () => props.value,
      (newValue) => {
        selected.value = [...newValue];
      },
      { immediate: true },
    );

    const itemIsSelected = (name: string): boolean => selected.value.includes(name);

    const emitSelection = (): void => {
      const nextSelected = [...selected.value];
      emit("input", nextSelected);
      emit("handleInput", nextSelected);
    };

    const handleSelect = (name: string): void => {
      if (!props.isChecklist) {
        return;
      }

      if (itemIsSelected(name)) {
        selected.value = selected.value.filter((selectedItem) => selectedItem !== name);
      } else {
        selected.value = [...selected.value, name];
      }

      emitSelection();
    };

    const checkboxIcon = (name: string): string => {
      return itemIsSelected(name) ? mdiCheckboxOutline : mdiCheckboxBlankOutline;
    };

    const toggleExpanded = (): void => {
      isExpanded.value = !isExpanded.value;
    };

    return {
      isExpanded,
      mdiChevronDown,
      mdiCircle,
      checkboxIcon,
      handleSelect,
      toggleExpanded,
    };
  },
})
</script>
